/**
 * The Navigate tools: Basemap, Legend, Coordinates, Directions.
 *
 * Each reads its saved settings from `ctx.config`, which is what makes the
 * questions the Tool Library asks mean something — before this, an admin could
 * pick which basemaps to offer or how many decimals to show and the map ignored
 * every answer.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Crosshair, Loader2, MapPin } from 'lucide-react';
import ToolPanelHeader, { ToolActions } from '../ToolPanel';
import { cfg, type BasemapId, type ToolContext } from './types';
import {
  CopyValue,
  EmptyHint,
  Field,
  Notice,
  ResultHeader,
  ResultRow,
  ScrollList,
  btnClass,
  inputClass,
} from './shared';
import { getLayerDisplayName } from '../../../layers/capabilities';
import { arcgisServiceUrlFor, getArcgisBaseUrl, withArcgisToken } from '../../../utils/arcgis';
import { locationSearchService, type LocationResult } from '../../../services/locationSearchService';
import { formatDuration, routeBetween, type RouteResult } from '../../../services/routingService';

/* ────────────────────────────── Basemap ─────────────────────────────────── */

const BASEMAP_CHOICES: { id: BasemapId; name: string; blurb: string }[] = [
  { id: 'street', name: 'Streets', blurb: 'OpenStreetMap' },
  { id: 'terrain', name: 'Topographic', blurb: 'OpenTopoMap' },
  { id: 'satellite', name: 'Imagery', blurb: 'Esri World Imagery' },
];

export function BasemapPanel({ ctx }: { ctx: ToolContext }) {
  // An empty allow-list means "offer everything", the same rule the layer
  // scoping uses — an admin who configured nothing gets the full gallery.
  const allowed = cfg<string[]>(ctx.config, 'basemaps', []);
  const choices =
    allowed.length > 0
      ? BASEMAP_CHOICES.filter((choice) => allowed.includes(choice.id))
      : BASEMAP_CHOICES;

  return (
    <>
      <ToolPanelHeader
        title="Basemap"
        description="The reference map your layers are drawn over."
        onClose={ctx.onClose}
      />
      {choices.length === 0 ? (
        <EmptyHint>No basemaps are enabled for this tool. Add one in the Tool Library.</EmptyHint>
      ) : (
        <div className="space-y-1">
          {choices.map((choice) => {
            const active = ctx.basemap === choice.id;
            return (
              <ResultRow
                key={choice.id}
                title={choice.name}
                detail={choice.blurb}
                active={active}
                onClick={() => ctx.onBasemapChange?.(choice.id)}
                action={
                  active ? (
                    <span className="flex-shrink-0 text-[10px] font-semibold uppercase tracking-wide text-blue-600">
                      Current
                    </span>
                  ) : undefined
                }
              />
            );
          })}
        </div>
      )}
    </>
  );
}

/* ─────────────────────────────── Legend ─────────────────────────────────── */

/** One layer's symbol swatches, however its service publishes them. */
function LegendEntry({ layer, ctx }: { layer: any; ctx: ToolContext }) {
  const [swatches, setSwatches] = useState<{ label: string; image: string }[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;

    // ArcGIS publishes a legend endpoint for the whole service at once; WMS
    // renders one image per layer through GetLegendGraphic.
    const load = async () => {
      if (layer.arcgisLayerId === undefined) {
        setSwatches(null);
        return;
      }
      const effectiveUrl = arcgisServiceUrlFor(layer, { arcgisUrl: ctx.arcgisUrl });
      const baseUrl = getArcgisBaseUrl(effectiveUrl);
      if (!baseUrl) return;
      try {
        const response = await fetch(withArcgisToken(`${baseUrl}/legend?f=json`, effectiveUrl));
        if (!response.ok) throw new Error('no legend');
        const data = await response.json();
        const entry = (data?.layers ?? []).find((item: any) => item?.layerId === layer.arcgisLayerId);
        if (!live) return;
        setSwatches(
          (entry?.legend ?? []).map((item: any) => ({
            label: item?.label || '',
            image: `data:${item?.contentType ?? 'image/png'};base64,${item?.imageData ?? ''}`,
          })),
        );
      } catch {
        if (live) setFailed(true);
      }
    };

    void load();
    return () => {
      live = false;
    };
  }, [layer.arcgisLayerId, layer.arcgisServiceUrl, ctx.arcgisUrl]);

  const wmsLegendUrl = useMemo(() => {
    if (layer.arcgisLayerId !== undefined || !ctx.geoServerUrl) return '';
    try {
      const url = new URL(ctx.geoServerUrl.trim());
      url.search = '';
      url.searchParams.set('service', 'WMS');
      url.searchParams.set('version', '1.1.0');
      url.searchParams.set('request', 'GetLegendGraphic');
      url.searchParams.set('format', 'image/png');
      url.searchParams.set('layer', layer.name);
      return url.toString();
    } catch {
      return '';
    }
  }, [ctx.geoServerUrl, layer.arcgisLayerId, layer.name]);

  return (
    <div className="border-b border-slate-100 pb-2">
      <p className="truncate text-[12px] font-medium text-slate-700" title={layer.name}>
        {getLayerDisplayName(layer)}
      </p>
      {swatches && swatches.length > 0 && (
        <ul className="mt-1 space-y-0.5">
          {swatches.map((swatch, index) => (
            <li key={`${swatch.label}-${index}`} className="flex items-center gap-1.5">
              <img src={swatch.image} alt="" className="h-3.5 w-3.5 flex-shrink-0 object-contain" />
              <span className="min-w-0 truncate text-[11px] text-slate-500">
                {swatch.label || '—'}
              </span>
            </li>
          ))}
        </ul>
      )}
      {wmsLegendUrl && (
        <img
          src={wmsLegendUrl}
          alt={`${layer.name} symbols`}
          className="mt-1 max-h-14 max-w-full object-contain object-left"
          onError={(event) => {
            (event.currentTarget as HTMLImageElement).style.display = 'none';
          }}
        />
      )}
      {(failed || (swatches !== null && swatches.length === 0)) && !wmsLegendUrl && (
        <p className="mt-0.5 text-[10.5px] text-slate-400">
          This service publishes no legend for the layer.
        </p>
      )}
    </div>
  );
}

export function LegendPanel({ ctx }: { ctx: ToolContext }) {
  const onlyVisible = cfg<boolean>(ctx.config, 'onlyVisible', true);
  const shown = ctx.layers.filter(
    (layer) => (layer.showInLegend ?? true) && (!onlyVisible || layer.checked),
  );

  return (
    <>
      <ToolPanelHeader
        title="Legend"
        description={
          onlyVisible
            ? 'Symbols for the layers currently switched on.'
            : 'Symbols for every layer in this map.'
        }
        onClose={ctx.onClose}
      />
      {shown.length === 0 ? (
        <EmptyHint>
          {onlyVisible
            ? 'Nothing is switched on. Tick a layer in the Layers tab to see its symbols.'
            : 'This map has no layers yet.'}
        </EmptyHint>
      ) : (
        <ScrollList max={360}>
          {shown.map((layer) => (
            <LegendEntry key={layer.id} layer={layer} ctx={ctx} />
          ))}
        </ScrollList>
      )}
    </>
  );
}

/* ──────────────────────────── Coordinates ───────────────────────────────── */

/** Degrees-minutes-seconds, the way a survey sheet writes it. */
function toDms(value: number, positive: string, negative: string) {
  const hemisphere = value >= 0 ? positive : negative;
  const abs = Math.abs(value);
  const degrees = Math.floor(abs);
  const minutesFull = (abs - degrees) * 60;
  const minutes = Math.floor(minutesFull);
  const seconds = (minutesFull - minutes) * 60;
  return `${degrees}° ${String(minutes).padStart(2, '0')}' ${seconds.toFixed(2).padStart(5, '0')}" ${hemisphere}`;
}

/** One place someone marked, kept only while the tool is open. */
interface DroppedPoint {
  id: number;
  lat: number;
  lng: number;
}

export function CoordinatesPanel({ ctx }: { ctx: ToolContext }) {
  const format = cfg<string>(ctx.config, 'format', 'dd');
  const decimals = Math.max(0, Math.min(10, cfg<number>(ctx.config, 'decimals', 5)));
  const [typed, setTyped] = useState('');
  const [error, setError] = useState<string | null>(null);

  /* ── Dropped points ──────────────────────────────────────────────────
     Deliberately NOT a temporary layer like the Buffer tool's. A buffer is a
     result someone wants to keep and restyle; these are scratch marks made
     while reading coordinates off a map, and they go when the tool does. That
     is what the overlay channel is for. */
  const [points, setPoints] = useState<DroppedPoint[]>([]);
  const [collecting, setCollecting] = useState(false);
  /** The click already consumed, so one click cannot drop two pins. */
  const lastConsumed = useRef<string>('');

  const point = ctx.pointer;

  const write = (lat: number, lng: number) =>
    format === 'dms'
      ? `${toDms(lat, 'N', 'S')}, ${toDms(lng, 'E', 'W')}`
      : `${lat.toFixed(decimals)}, ${lng.toFixed(decimals)}`;

  /* Every map click while collecting drops a pin. Guarded on the coordinate
     itself rather than a counter: `pointer` is shared state that re-renders
     this panel for reasons of its own, and a re-render is not a new click. */
  useEffect(() => {
    if (!collecting || !point) return;
    const signature = `${point.lat},${point.lng}`;
    if (signature === lastConsumed.current) return;
    lastConsumed.current = signature;
    setPoints((current) => [...current, { id: Date.now() + current.length, lat: point.lat, lng: point.lng }]);
  }, [collecting, point]);

  /* The map shows what the list holds — one source, drawn twice. */
  useEffect(() => {
    ctx.onOverlayChange?.(
      points.length === 0
        ? null
        : {
            type: 'FeatureCollection',
            features: points.map((entry, index) => ({
              type: 'Feature' as const,
              properties: { marker: index + 1, label: write(entry.lat, entry.lng) },
              geometry: { type: 'Point' as const, coordinates: [entry.lng, entry.lat] },
            })),
          },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points, format, decimals]);

  /* Closing the tool takes its marks with it — that is the whole contract of a
     scratch mark, and the reason these are not layers. */
  useEffect(
    () => () => ctx.onOverlayChange?.(null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const written = point
    ? format === 'dms'
      ? { lat: toDms(point.lat, 'N', 'S'), lng: toDms(point.lng, 'E', 'W') }
      : { lat: point.lat.toFixed(decimals), lng: point.lng.toFixed(decimals) }
    : null;

  /* Accepts what people actually paste: "lat, lng" or "lat lng", either order
     of separators. Anything else is refused with a reason rather than silently
     moving the map somewhere wrong. */
  const go = () => {
    const parts = typed.split(/[,\s]+/).map((part) => Number(part.trim())).filter((n) => !Number.isNaN(n));
    if (parts.length < 2) {
      setError('Enter a latitude and a longitude, separated by a comma.');
      return;
    }
    const [lat, lng] = parts;
    if (Math.abs(lat) > 90 || Math.abs(lng) > 180) {
      setError('Latitude must be within ±90 and longitude within ±180.');
      return;
    }
    setError(null);
    setPoints((current) => [...current, { id: Date.now() + current.length, lat, lng }]);
    ctx.onGoToLocation([lat, lng], Math.max(ctx.mapZoom, 15));
  };

  return (
    <>
      <ToolPanelHeader
        title="Coordinates"
        description="Read a location off the map, or jump to one."
        onClose={ctx.onClose}
      />

      {written ? (
        <div className="space-y-1.5">
          <CopyValue label="Latitude" value={written.lat} />
          <CopyValue label="Longitude" value={written.lng} />
          <CopyValue
            label="Both"
            value={
              format === 'dms'
                ? `${written.lat}, ${written.lng}`
                : `${point!.lat.toFixed(decimals)}, ${point!.lng.toFixed(decimals)}`
            }
          />
        </div>
      ) : (
        <EmptyHint>Click anywhere on the map to read its coordinates here.</EmptyHint>
      )}

      <Field label="Go to coordinates" hint="Latitude, longitude — for example 41.7790, -88.1467">
        <div className="flex gap-1.5">
          <input
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
            onKeyDown={(event) => event.key === 'Enter' && go()}
            placeholder="41.7790, -88.1467"
            className={inputClass}
          />
          <button type="button" onClick={go} className={`${btnClass} gl-btn--primary`}>
            Go
          </button>
        </div>
      </Field>
      {error && <Notice tone="warn">{error}</Notice>}
    </>
  );
}

/* ───────────────────────────── Directions ───────────────────────────────── */

const EARTH_RADIUS_KM = 6371.0088;

/** Great-circle distance, the shortest path over the globe between two points. */
function haversineKm(a: { lat: number; lng: number }, b: { lat: number; lng: number }) {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Initial bearing, in degrees clockwise from north. */
function bearing(a: { lat: number; lng: number }, b: { lat: number; lng: number }) {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLng = toRad(b.lng - a.lng);
  const y = Math.sin(dLng) * Math.cos(toRad(b.lat));
  const x =
    Math.cos(toRad(a.lat)) * Math.sin(toRad(b.lat)) -
    Math.sin(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.cos(dLng);
  return (((Math.atan2(y, x) * 180) / Math.PI) + 360) % 360;
}

/** A road distance in the unit the tool is configured for. */
function formatRoadDistance(metres: number, unit: string): string {
  if (unit === 'mi') return `${(metres / 1609.344).toFixed(2)} mi`;
  if (unit === 'm') return `${Math.round(metres).toLocaleString()} m`;
  return `${(metres / 1000).toFixed(2)} km`;
}

const COMPASS = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];

/** A place picked for one end of the line. Exported so another tool (Near
 *  Me's own "Directions from here" button) can hand DirectionsPanel a
 *  starting pair through ToolContext.directionsSeed without re-declaring
 *  this shape. */
export interface Waypoint {
  label: string;
  lat: number;
  lng: number;
}

function PlaceInput({
  label,
  value,
  onPick,
  picking,
  onTogglePicking,
}: {
  label: string;
  value: Waypoint | null;
  onPick: (point: Waypoint | null) => void;
  /** True while THIS field (not necessarily the other one) is waiting for a
   *  map click — From and To share one "armed" slot in the parent, so
   *  starting one cancels the other rather than leaving both listening for
   *  the same next click. */
  picking: boolean;
  onTogglePicking: () => void;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<LocationResult[]>([]);
  const [busy, setBusy] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (query.trim().length < 3) {
      setResults([]);
      return undefined;
    }
    // Debounced, and the previous request is aborted — otherwise a slower
    // earlier response lands after a newer one and overwrites it.
    const timer = window.setTimeout(() => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setBusy(true);
      void locationSearchService
        .search(query, controller.signal)
        .then((found) => setResults(found.slice(0, 5)))
        .catch(() => undefined)
        .finally(() => setBusy(false));
    }, 350);
    return () => window.clearTimeout(timer);
  }, [query]);

  if (value) {
    return (
      <Field label={label}>
        <ResultRow
          title={value.label}
          detail={`${value.lat.toFixed(4)}, ${value.lng.toFixed(4)}`}
          action={
            <button
              type="button"
              onClick={() => {
                onPick(null);
                setQuery('');
              }}
              className="flex-shrink-0 text-[10.5px] font-semibold text-blue-600 hover:text-blue-800"
            >
              Change
            </button>
          }
        />
      </Field>
    );
  }

  return (
    <Field label={label}>
      <div className="flex gap-1.5">
        <div className="relative min-w-0 flex-1">
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={picking ? 'Click the map…' : 'Search for a place…'}
            disabled={picking}
            className={`${inputClass} disabled:bg-slate-50 disabled:text-slate-400`}
          />
          {busy && (
            <Loader2 className="absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 animate-spin text-slate-400" />
          )}
        </div>
        <button
          type="button"
          onClick={onTogglePicking}
          title={picking ? 'Cancel picking from the map' : 'Pick this point from the map'}
          aria-pressed={picking}
          className={`flex-shrink-0 rounded-lg border px-2.5 py-1.5 transition ${
            picking
              ? 'border-blue-400 bg-blue-50 text-blue-700'
              : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:text-slate-700'
          }`}
        >
          <Crosshair className="h-4 w-4" />
        </button>
      </div>
      {picking && (
        <p className="mt-1 text-[10.5px] leading-snug text-blue-600">
          Click anywhere on the map to set {label.toLowerCase()}.
        </p>
      )}
      {results.length > 0 && (
        <div className="mt-1 space-y-1">
          {results.map((result) => (
            <ResultRow
              key={result.id}
              title={result.label}
              detail={result.displayName}
              onClick={() => {
                onPick({ label: result.label, lat: result.latitude, lng: result.longitude });
                setResults([]);
              }}
            />
          ))}
        </div>
      )}
    </Field>
  );
}

export function DirectionsPanel({ ctx }: { ctx: ToolContext }) {
  const unit = cfg<string>(ctx.config, 'unit', 'km');
  const showSteps = cfg<boolean>(ctx.config, 'showSteps', true);
  const [from, setFrom] = useState<Waypoint | null>(null);
  const [to, setTo] = useState<Waypoint | null>(null);
  const [route, setRoute] = useState<RouteResult | null>(null);
  const [routing, setRouting] = useState(false);
  const [routeError, setRouteError] = useState<string | null>(null);

  /** Which end is waiting for a map click — From and To share this one slot,
   *  so arming one cancels the other rather than leaving both listening for
   *  the same next click. */
  const [picking, setPicking] = useState<'from' | 'to' | null>(null);
  const pickConsumedRef = useRef('');

  /* A map click sets whichever end is armed. Guarded on the coordinate, not
     a counter: `pointer` re-renders this panel for its own reasons, and a
     re-render is not a new click — the same pattern the Select/Trace tools'
     own point-placement already uses. No reverse geocoder exists in this
     app, so a map-picked point is labelled by its own coordinates, same as
     Asset Search's own incident flag. */
  useEffect(() => {
    if (!picking || !ctx.pointer) return;
    const clicked = ctx.pointer;
    const signature = `${picking}:${clicked.lat},${clicked.lng}`;
    if (signature === pickConsumedRef.current) return;
    pickConsumedRef.current = signature;
    const point: Waypoint = {
      label: `${clicked.lat.toFixed(5)}, ${clicked.lng.toFixed(5)}`,
      lat: clicked.lat,
      lng: clicked.lng,
    };
    if (picking === 'from') setFrom(point);
    else setTo(point);
    setPicking(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picking, ctx.pointer]);

  const measured = from && to ? { km: haversineKm(from, to), heading: bearing(from, to) } : null;

  /* Near Me's "Directions from here" button hands a From/To pair in through
     ToolContext rather than this panel reading anything Near-Me-specific —
     any future "open Directions pre-filled" caller uses the exact same
     channel. Keyed on the token, not the values, so pressing the button
     again for the SAME pair still re-seeds (e.g. after the admin cleared
     the fields by hand). */
  const seededTokenRef = useRef<number | null>(null);
  useEffect(() => {
    const seed = ctx.directionsSeed;
    if (!seed || seed.token === seededTokenRef.current) return;
    seededTokenRef.current = seed.token;
    setFrom(seed.from);
    setTo(seed.to);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.directionsSeed]);

  /* Asks for the road route as soon as both ends are known.
     The straight line stays as the fallback rather than the goal: when the
     router cannot answer, a measured direct line is still useful and honest,
     where an empty panel would just be a dead end. */
  useEffect(() => {
    if (!from || !to) {
      setRoute(null);
      setRouteError(null);
      return;
    }

    let live = true;
    setRouting(true);
    setRouteError(null);

    void routeBetween(from, to)
      .then((result) => {
        if (live) setRoute(result);
      })
      .catch((error) => {
        if (!live) return;
        setRoute(null);
        setRouteError(error?.message ?? 'The route could not be built.');
      })
      .finally(() => {
        if (live) setRouting(false);
      });

    return () => {
      live = false;
    };
  }, [from, to]);

  /* Draws whichever line the numbers describe — the road when there is one,
     the direct line when there is not. The map and the panel must never be
     showing different answers. */
  useEffect(() => {
    if (!from || !to) {
      ctx.onOverlayChange?.(null);
      return;
    }
    const coordinates: number[][] =
      route && route.coordinates.length > 1
        ? route.coordinates
        : [[from.lng, from.lat], [to.lng, to.lat]];

    ctx.onOverlayChange?.({
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          properties: { role: 'direction' },
          geometry: { type: 'LineString', coordinates },
        },
        {
          type: 'Feature',
          properties: { marker: 'A', label: from.label },
          geometry: { type: 'Point', coordinates: [from.lng, from.lat] },
        },
        {
          type: 'Feature',
          properties: { marker: 'B', label: to.label },
          geometry: { type: 'Point', coordinates: [to.lng, to.lat] },
        },
      ],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, route]);

  useEffect(
    () => () => ctx.onOverlayChange?.(null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const distance =
    measured &&
    (unit === 'mi'
      ? `${(measured.km * 0.621371).toFixed(2)} mi`
      : unit === 'm'
        ? `${(measured.km * 1000).toFixed(0)} m`
        : `${measured.km.toFixed(2)} km`);

  return (
    <>
      <ToolPanelHeader
        title="Directions"
        description="A driving route between two places, with turn-by-turn directions."
        onClose={ctx.onClose}
      />

      <PlaceInput
        label="From"
        value={from}
        onPick={(point) => {
          setFrom(point);
          setPicking(null);
        }}
        picking={picking === 'from'}
        onTogglePicking={() => setPicking((current) => (current === 'from' ? null : 'from'))}
      />
      <PlaceInput
        label="To"
        value={to}
        onPick={(point) => {
          setTo(point);
          setPicking(null);
        }}
        picking={picking === 'to'}
        onTogglePicking={() => setPicking((current) => (current === 'to' ? null : 'to'))}
      />

      {routing && (
        <p className="flex items-center gap-2 text-[12px] text-slate-500">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Finding a road route…
        </p>
      )}

      {route && (
        <div className="space-y-1.5">
          <div className="grid grid-cols-2 gap-1.5">
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                By road
              </p>
              <p className="font-mono text-[13px] font-semibold tabular-nums text-slate-700">
                {formatRoadDistance(route.metres, unit)}
              </p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                Driving time
              </p>
              <p className="font-mono text-[13px] font-semibold tabular-nums text-slate-700">
                {formatDuration(route.seconds)}
              </p>
            </div>
          </div>

          {showSteps && route.steps.length > 0 && (
            <div className="space-y-1">
              <ResultHeader label="Directions" count={route.steps.length} />
              <ScrollList max={220}>
                {route.steps.map((step, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-2 rounded-lg border border-slate-200 bg-white px-2 py-1.5"
                  >
                    <span className="mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-slate-200 text-[9px] font-bold text-slate-600">
                      {index + 1}
                    </span>
                    <span className="min-w-0 flex-1 text-[11.5px] leading-snug text-slate-700">
                      {step.instruction}
                    </span>
                    <span className="flex-shrink-0 text-[10.5px] tabular-nums text-slate-400">
                      {step.metres < 1000
                        ? `${Math.round(step.metres)} m`
                        : `${(step.metres / 1000).toFixed(1)} km`}
                    </span>
                  </div>
                ))}
              </ScrollList>
            </div>
          )}
        </div>
      )}

      {routeError && (
        <Notice tone="warn">
          {routeError} Showing the direct line instead.
        </Notice>
      )}

      {measured && distance ? (
        <div className="space-y-1.5">
          <CopyValue label={route ? 'Straight-line distance' : 'Distance'} value={distance} />
          <CopyValue
            label="Heading"
            value={`${measured.heading.toFixed(1)}° ${COMPASS[Math.round(measured.heading / 22.5) % 16]}`}
          />
          <ToolActions>
            <button
              type="button"
              onClick={() => {
                const midpoint: [number, number] = [
                  (from!.lat + to!.lat) / 2,
                  (from!.lng + to!.lng) / 2,
                ];
                const span = Math.max(
                  Math.abs(from!.lat - to!.lat),
                  Math.abs(from!.lng - to!.lng),
                  0.002,
                );
                ctx.onGoToLocation(midpoint, Math.max(2, Math.min(16, Math.floor(Math.log2(360 / span)))));
              }}
              className={`${btnClass} gl-btn--primary`}
            >
              <MapPin className="h-3.5 w-3.5" />
              Show both
            </button>
            <button
              type="button"
              onClick={() => {
                setFrom(null);
                setTo(null);
                setRoute(null);
                setRouteError(null);
              }}
              className={`${btnClass} gl-btn--secondary`}
            >
              Clear
            </button>
          </ToolActions>
        </div>
      ) : (
        <EmptyHint>Pick both ends to see the distance and heading.</EmptyHint>
      )}
    </>
  );
}
